import { useBitcoinData } from '@/hooks/useBitcoinData';
import { useBuddyBackend } from '@/hooks/useBuddyBackend';
import { useBuddyVisuals } from '@/hooks/useBuddyVisuals';
import { useTransientReactions } from '@/hooks/useTransientReactions';
import BuddyCharacter from '@/components/BuddyCharacter';
import SceneBackground from '@/components/SceneBackground';
import IslandSign from '@/components/IslandSign';
import IslandBarometer from '@/components/IslandBarometer';
import EmotionalLighting, { useBuddyMacroFilter } from '@/components/EmotionalLighting';
import type { MacroLightState } from '@/lib/lighting/emotionalLightingEngine';
import { deriveSceneLighting } from '@/lib/lighting/emotionalLightingEngine';

import MoodBanner from '@/components/MoodBanner';
import { deriveMacroFromBackendState } from '@/lib/state/macroAdvisorEngine';
import { resolveDisplayMacroState, MACRO_DISPLAY_COPY, selectDailyFooterLine, type MacroDisplayState } from '@/config/macroDisplayCopy';

import PriceCard from '@/components/PriceCard';
import BlockCard from '@/components/BlockCard';

import EventFeed from '@/components/EventFeed';
import InfoDashboard from '@/components/InfoDashboard';
import BuddyThoughtTicker from '@/components/BuddyThoughtTicker';
import { motion, AnimatePresence } from 'framer-motion';
import type { NewsItem } from '@/hooks/useBuddyBackend';
import { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { useOnboarding } from '@/components/OnboardingSheet';
import { useStackSatsGame } from '@/hooks/useStackSatsGame';
import { toast } from 'sonner';
import { lazy, Suspense } from 'react';

const ShareModal = lazy(() => import('@/components/ShareModal'));
const OnboardingSheet = lazy(() => import('@/components/OnboardingSheet'));
const BuddyGuide = lazy(() => import('@/components/BuddyGuide'));
const MatrixRain = lazy(() => import('@/components/MatrixRain'));
const MatrixSplash = lazy(() => import('@/components/MatrixSplash'));
const StackSatsGame = lazy(() => import('@/components/StackSatsGame'));

const Index = () => {
  const backend = useBuddyBackend();
  const { network, blockJustFound: realBlockJustFound, loading, error } = useBitcoinData();
  const transientManager = useTransientReactions();
  const [simulateBlock, setSimulateBlock] = useState(false);
  const blockJustFound = realBlockJustFound || simulateBlock;

  // Auto-clear simulated block
  useEffect(() => {
    if (simulateBlock) {
      const t = setTimeout(() => setSimulateBlock(false), 4000);
      return () => clearTimeout(t);
    }
  }, [simulateBlock]);

  // Trigger transient on block found
  const prevBlockHeight = useRef(0);
  useEffect(() => {
    if (realBlockJustFound && network.lastBlockHeight > 0 && network.lastBlockHeight !== prevBlockHeight.current) {
      prevBlockHeight.current = network.lastBlockHeight;
      transientManager.triggerTransient('blockFound');
    }
  }, [realBlockJustFound, network.lastBlockHeight]);

  // Also trigger transient on simulated block
  useEffect(() => {
    if (simulateBlock) {
      transientManager.triggerTransient('blockFound');
    }
  }, [simulateBlock]);

  // ── New transient triggers ──

  // Fear surge: sentiment drops to extreme_fear
  const prevSentimentRef = useRef<string | null>(null);
  useEffect(() => {
    const bucket = backend.state?.sentimentBucket ?? null;
    if (bucket && prevSentimentRef.current && bucket !== prevSentimentRef.current) {
      if (bucket === 'extreme_fear') {
        transientManager.triggerTransient('fearSurge');
      } else if (bucket === 'extreme_greed') {
        transientManager.triggerTransient('greedPeak');
      }
    }
    prevSentimentRef.current = bucket;
  }, [backend.state?.sentimentBucket]);

  // Fee relief: medium fee drops >30% from recent high
  const prevMedFeeRef = useRef(0);
  useEffect(() => {
    const currentFee = network.recommendedFees.medium;
    if (prevMedFeeRef.current > 0 && currentFee > 0) {
      const dropPct = (prevMedFeeRef.current - currentFee) / prevMedFeeRef.current;
      if (dropPct > 0.3) {
        transientManager.triggerTransient('feeRelief');
      }
    }
    prevMedFeeRef.current = Math.max(prevMedFeeRef.current, currentFee);
  }, [network.recommendedFees.medium]);

  // Drought anxiety: >18 minutes since last block
  const droughtFiredRef = useRef(false);
  useEffect(() => {
    const timeSince = network.timeSinceLastBlock ?? 0;
    if (timeSince > 1080 && !droughtFiredRef.current) {
      droughtFiredRef.current = true;
      transientManager.triggerTransient('droughtAnxiety');
    }
    if (timeSince < 120) {
      droughtFiredRef.current = false;
    }
  }, [network.timeSinceLastBlock]);

  const { composed, environment, heatLevel, activeSignature } = useBuddyVisuals(
    backend.state,
    transientManager.activeTransient,
  );

  const [showDebug, setShowDebug] = useState(false);
  const toggleDebug = useCallback(() => setShowDebug(d => !d), []);
  const [showInfo, setShowInfo] = useState(false);
  const onboarding = useOnboarding();
  const [hudExpanded, setHudExpanded] = useState(false);
  const [showBarometer, setShowBarometer] = useState(false);
  const [shareCapture, setShareCapture] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  
  const [showGuide, setShowGuide] = useState(false);
  const [matrixMode, setMatrixMode] = useState(false);
  const [matrixSplash, setMatrixSplash] = useState(false);



  // Stack Sats game
  const stackSats = useStackSatsGame(
    network.mempoolCongestion,
    blockJustFound,
    network.lastBlockHeight,
  );

  const sceneRef = useRef<HTMLDivElement>(null);
  const buddyCaptureRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));


  useEffect(() => {
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const buddyScale = viewport.width <= 768
    ? Math.min(1.5, Math.max(1.22, viewport.width / 280))
    : Math.min(1.6, Math.max(1.05, Math.min(viewport.width / 340, viewport.height / 540 + 0.35)));

  const bs = backend.state;
  const hasBackend = !!bs && !backend.loading;

  const displayEvents = hasBackend && backend.events.length > 0
    ? backend.events.slice(0, 3)
    : [];

  const displayVerdict = bs?.verdict ?? 'Observing conditions.';
  const displayArchetype = bs?.dayArchetype ?? 'Calm Chain Day';
  const displayWeather = bs?.weather ?? 'clear';
  const displayWeatherDesc = bs?.weatherDescription ?? 'Clear skies on-chain.';

  // New macro display hierarchy
  const displayMacroState: MacroDisplayState = useMemo(() => {
    if (bs?.displayMacroState && bs.displayMacroState in MACRO_DISPLAY_COPY) {
      return bs.displayMacroState as MacroDisplayState;
    }
    return resolveDisplayMacroState(
      bs?.macroAdvisor?.macroLabel,
      bs?.macroMode,
    );
  }, [bs?.displayMacroState, bs?.macroAdvisor?.macroLabel, bs?.macroMode]);

  const displayMoodLabel = bs?.moodLabel || MACRO_DISPLAY_COPY[displayMacroState].moodLabel;
  const displayPlainSubtitle = bs?.plainSubtitle || MACRO_DISPLAY_COPY[displayMacroState].plainSubtitle;
  const displayMacroExplanation = bs?.macroExplanation || MACRO_DISPLAY_COPY[displayMacroState].explanation;
  const displayWhatItMeans = bs?.whatItMeans || MACRO_DISPLAY_COPY[displayMacroState].whatItMeans;
  const displayFooterLine = bs?.footerLine || selectDailyFooterLine(MACRO_DISPLAY_COPY[displayMacroState].footerLines);

  const confidenceLabel = !hasBackend ? 'CONNECTING'
    : bs.ltConfidence === 'high' ? 'TUNED IN'
    : bs.ltConfidence === 'medium' ? 'WARMING UP'
    : 'OBSERVING';

  const isStale = hasBackend ? bs.isStale : false;

  const handleShare = useCallback(async () => {
    if (shareLoading) return;
    setShareLoading(true);
    setHudExpanded(false);
    await new Promise(r => setTimeout(r, 150));

    try {
      // Capture full scene but exclude all UI overlays (header, footer, HUD, debug)
      const target = sceneRef.current;
      if (!target) throw new Error('No scene ref');

      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(target, {
        scale: 3,
        useCORS: true,
        backgroundColor: null,
        logging: false,
        ignoreElements: (el) => {
          if (el.getAttribute('data-share-exclude') === 'true') return true;
          // Exclude header, footer, and z-10 UI overlays
          if (el.tagName === 'HEADER' || el.tagName === 'FOOTER') return true;
          return false;
        },
      });
      setShareCapture(canvas.toDataURL('image/png'));
    } catch {
      const text = `Bitcoin network right now: ${displayArchetype} — ${displayVerdict}\nPrice: $${network.btcPrice.toLocaleString()} · Fees: ${network.recommendedFees.medium} sat/vB · Block #${network.lastBlockHeight.toLocaleString()}\n🔗 ${window.location.href}`;
      try {
        await navigator.clipboard.writeText(text);
        toast('Image capture failed — link copied instead');
      } catch {
        toast('Could not capture or copy');
      }
    } finally {
      setShareLoading(false);
    }
  }, [shareLoading, network, displayArchetype, displayVerdict]);

  // Macro advisory — prefer real backend data, fall back to derived
  const congestion = bs?.heatLevel ?? 0;
  const macroAdvisory = useMemo(() => {
    if (bs?.macroAdvisor) return bs.macroAdvisor;
    return deriveMacroFromBackendState(
      bs?.macroMode ?? 'NEAR_200WMA',
      bs?.seasonState ?? 'neutral',
      congestion,
      bs?.ltConfidence ?? 'low',
    );
  }, [bs?.macroAdvisor, bs?.macroMode, bs?.seasonState, congestion, bs?.ltConfidence]);

  if (backend.loading && network.btcPrice === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center noise-overlay">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center space-y-4"
        >
          <motion.div
            className="text-4xl"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            ⛏️
          </motion.div>
          <p className="font-pixel text-[10px] text-bitcoin text-glow tracking-wider">
            Mempool Buddy
          </p>
          <p className="text-[10px] text-muted-foreground/60 font-body font-light">
            Connecting to the Bitcoin network...
          </p>
          <motion.div
            className="mx-auto h-px w-24 rounded-full overflow-hidden"
            style={{ background: 'hsl(var(--border))' }}
          >
            <motion.div
              className="h-full w-12 rounded-full"
              style={{ background: 'hsl(var(--bitcoin) / 0.5)' }}
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Error
  if (backend.error && error && network.btcPrice === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center noise-overlay">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center space-y-3 px-8"
        >
          <div className="text-3xl">😵‍💫</div>
          <p className="font-pixel text-[9px] text-bitcoin/70 tracking-wider">
            Connection lost
          </p>
          <p className="text-[10px] text-muted-foreground/50 font-body font-light max-w-[200px]">
            Couldn't reach the network. Buddy will reconnect automatically.
          </p>
        </motion.div>
      </div>
    );
  }

  const networkState = (bs?.networkState ?? 'quiet') as any;
  const blockState = (bs?.blockState ?? 'normal') as any;
  const priceState = (bs?.priceState ?? 'flat') as any;

  // Fee heat: ratio of current medium fee to a "calm" baseline
  const feeHeat = Math.min(1, network.recommendedFees.medium / 80);

  return (
    <Suspense fallback={null}>
    <div ref={sceneRef} className="min-h-screen relative flex flex-col items-center overflow-hidden" style={{ background: matrixMode ? '#000' : 'hsl(var(--background))' }}>
      <AnimatePresence>
        {matrixSplash && (
          <MatrixSplash
            key="matrix-splash"
            onTransition={() => setMatrixMode(true)}
            onComplete={() => setMatrixSplash(false)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {!matrixMode && (
          <motion.div
            key="island-bg"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 z-0"
          >
            <SceneBackground weather={displayWeather} seasonState={bs?.seasonState} seasonIntensity={bs?.seasonIntensity} heatLevel={heatLevel} minerActivity={environment?.minerActivity ?? 0.5} />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {matrixMode && (
          <MatrixRain
            active={matrixMode}
            congestion={network.mempoolCongestion}
            pendingTxCount={network.pendingTxCount}
            feeFast={network.recommendedFees.high}
            feeMedium={network.recommendedFees.medium}
            feeSlow={network.recommendedFees.low}
            blockHeight={network.lastBlockHeight}
            blockJustFound={blockJustFound}
            mempoolVsize={network.mempoolVsize}
            hashrateTHs={network.hashrateTHs}
          />
        )}
      </AnimatePresence>
      {/* Stack Sats minigame overlay */}
      {matrixMode && stackSats.phase !== 'idle' && (
        <StackSatsGame
          phase={stackSats.phase}
          score={stackSats.score}
          capturedPct={stackSats.capturedPct}
          elapsed={stackSats.elapsed}
          maxRoundSec={stackSats.maxRoundSec}
          result={stackSats.result}
          captures={stackSats.captures}
          tick={stackSats.tick}
          updateBuddyPos={stackSats.updateBuddyPos}
          onDismiss={stackSats.dismiss}
          onReplay={stackSats.startGame}
          onExit={stackSats.endGame}
        />
      )}
      <EmotionalLighting
        macroState={(displayMacroState || 'NEUTRAL') as MacroLightState}
        mempoolPressure={heatLevel}
        blockJustFound={blockJustFound}
        heatLevel={heatLevel}
      />

      {/* ── Soft atmospheric gradient overlay behind top text ── */}
      {!matrixMode && (
        <div
          data-share-exclude="true"
          className="absolute inset-x-0 top-0 z-[5] pointer-events-none"
          style={{
            height: '45%',
            background: 'linear-gradient(180deg, hsl(var(--background) / 0.65) 0%, hsl(var(--background) / 0.35) 40%, hsl(var(--background) / 0.08) 70%, transparent 100%)',
          }}
        />
      )}

      {/* ── Unified Top Capsule ── */}
      <AnimatePresence>
      {stackSats.phase === 'idle' && (
      <motion.header data-share-exclude="true" className="w-full max-w-sm mx-auto px-3 pt-2.5 pb-0 relative z-10"
        initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
          className="rounded-2xl overflow-hidden"
          style={{
            background: 'hsl(var(--foreground) / 0.07)',
            border: '1px solid hsl(var(--foreground) / 0.08)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 2px 12px hsl(var(--background) / 0.25), inset 0 1px 0 hsl(var(--foreground) / 0.04)',
          }}
        >
          {/* Row 1: Brand + expand toggle + Guidance */}
          <div className="flex items-center justify-between px-3.5 pt-2 pb-0.5">
            <button
              onClick={() => setHudExpanded(e => !e)}
              className="flex items-center gap-1.5 active:bg-foreground/[0.02] transition-colors"
            >
              <motion.p
                className="font-display text-[10px] font-semibold tracking-[0.1em] uppercase select-none whitespace-nowrap"
                style={{
                  color: 'hsl(var(--bitcoin) / 0.75)',
                  textShadow: '0 0 12px hsl(var(--bitcoin) / 0.12)',
                }}
                onDoubleClick={(e) => { e.stopPropagation(); toggleDebug(); }}
              >
                Mempool <span style={{ color: 'hsl(var(--bitcoin) / 0.9)' }}>₿</span>uddy
              </motion.p>
              <span
                className="text-[7px] font-body font-medium tracking-[0.12em] uppercase"
                style={{
                  color: 'hsl(var(--foreground) / 0.22)',
                  borderLeft: '1px solid hsl(var(--foreground) / 0.1)',
                  paddingLeft: 6,
                  marginLeft: 2,
                }}
              >
                live companion
              </span>
              <motion.span
                animate={{ rotate: hudExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="text-[8px] ml-0.5"
                style={{ color: 'hsl(var(--foreground) / 0.18)' }}
              >
                ▾
              </motion.span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowGuide(true); }}
              className="shrink-0 flex items-center gap-1 text-[9px] font-body font-semibold tracking-wider uppercase transition-colors px-1.5 py-0.5 rounded-md"
              style={{ color: 'hsl(var(--foreground) / 0.35)', background: 'hsl(var(--foreground) / 0.04)' }}
            >
              📖 <span>Guidance</span>
            </button>
          </div>

          {/* Row 2: Telemetry — Price / Fee / Block */}
          <button
            onClick={() => setHudExpanded(e => !e)}
            className="w-full grid grid-cols-3 items-center px-3.5 py-1 active:bg-foreground/[0.02] transition-colors"
          >
            {/* Left: BTC Price */}
            <div className="flex flex-col items-start gap-0">
              <div className="flex items-center gap-1">
                <motion.div
                  className="w-[5px] h-[5px] rounded-full shrink-0"
                  style={{
                    background: network.priceChange24h >= 0
                      ? 'hsl(var(--success) / 0.6)'
                      : 'hsl(var(--stress) / 0.6)',
                  }}
                  animate={{ scale: [1, 1.25, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <span
                  className="font-body font-bold text-[12px] tabular-nums tracking-tight"
                  style={{ color: 'hsl(var(--foreground) / 0.9)' }}
                >
                  ${network.btcPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </span>
              </div>
              <span
                className="text-[7px] font-body font-semibold tabular-nums ml-[9px]"
                style={{ color: network.priceChange24h >= 0 ? 'hsl(var(--success) / 0.65)' : 'hsl(var(--stress) / 0.65)' }}
              >
                {network.priceChange24h >= 0 ? '+' : ''}{network.priceChange24h.toFixed(1)}%
              </span>
            </div>

            {/* Center: Fee */}
            <div className="flex flex-col items-center gap-0">
              <span
                className="font-body font-bold text-[12px] tabular-nums"
                style={{ color: 'hsl(var(--foreground) / 0.75)' }}
              >
                {network.recommendedFees.medium}
              </span>
              <span
                className="text-[6px] font-body font-medium tracking-wider uppercase"
                style={{ color: 'hsl(var(--foreground) / 0.25)' }}
              >
                sat/vB
              </span>
            </div>

            {/* Right: Block */}
            <div className="flex flex-col items-end gap-0 relative">
              <AnimatePresence>
                {blockJustFound && (
                  <motion.div
                    className="absolute -inset-2 rounded-xl pointer-events-none"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
                    style={{
                      background: 'radial-gradient(ellipse at 60% 50%, hsl(var(--bitcoin) / 0.12) 0%, transparent 70%)',
                    }}
                  />
                )}
              </AnimatePresence>

              <div className="flex items-center gap-1">
                <div className="relative flex items-center justify-center" style={{ width: 8, height: 8 }}>
                  {blockJustFound ? (
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ background: 'hsl(var(--bitcoin) / 0.8)' }}
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: [0.5, 1.6, 1], opacity: [0, 1, 0.7] }}
                      transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                    />
                  ) : (
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ background: 'hsl(var(--foreground) / 0.15)' }}
                      animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.5, 0.3] }}
                      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  )}
                  <div
                    className="w-[4px] h-[4px] rounded-full relative z-10"
                    style={{
                      background: blockJustFound
                        ? 'hsl(var(--bitcoin) / 0.9)'
                        : 'hsl(var(--foreground) / 0.25)',
                      transition: 'background 0.4s ease',
                    }}
                  />
                </div>

                <AnimatePresence mode="wait">
                  <motion.span
                    key={network.lastBlockHeight}
                    initial={blockJustFound ? { opacity: 0, y: -4 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="font-body font-bold text-[10px] tabular-nums"
                    style={{
                      color: blockJustFound
                        ? 'hsl(var(--bitcoin) / 0.9)'
                        : 'hsl(var(--foreground) / 0.7)',
                      transition: 'color 1.5s ease',
                    }}
                  >
                    #{network.lastBlockHeight.toLocaleString()}
                  </motion.span>
                </AnimatePresence>
              </div>

              {/* Elapsed time */}
              {(() => {
                const elapsed = network.timeSinceLastBlock ?? 0;
                const mins = Math.floor(elapsed / 60);
                const secs = elapsed % 60;
                const overdue = elapsed > 600;
                const timerColor = overdue
                  ? 'hsl(var(--stress) / 0.55)'
                  : elapsed > 420
                    ? 'hsl(var(--bitcoin) / 0.4)'
                    : 'hsl(var(--foreground) / 0.28)';
                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="text-[7px] font-mono font-medium tabular-nums cursor-help"
                        style={{ color: timerColor }}
                      >
                        {mins}:{secs < 10 ? '0' : ''}{secs} ago
                      </button>
                    </PopoverTrigger>
                    <PopoverContent side="bottom" align="end" className="w-auto text-[10px] font-body px-3 py-2 rounded-lg space-y-1" style={{ background: 'hsl(var(--background) / 0.95)', border: '1px solid hsl(var(--foreground) / 0.1)' }}>
                      <p className="font-semibold" style={{ color: 'hsl(var(--foreground) / 0.8)' }}>⏱ Time since last block</p>
                      <div className="flex gap-3" style={{ color: 'hsl(var(--foreground) / 0.55)' }}>
                        <span>Avg: {Math.floor((network.avgBlockTimeSec ?? 600) / 60)}m {(network.avgBlockTimeSec ?? 600) % 60}s</span>
                        <span>·</span>
                        <span>Today: {network.blocksTodayCount ?? 0} blocks</span>
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              })()}
            </div>
          </button>

          {/* Signature reaction label — hidden */}

          {/* Row 3: Thought ticker divider + ticker */}
          <div
            className="mx-3"
            style={{ height: 1, background: 'linear-gradient(90deg, transparent, hsl(var(--foreground) / 0.06), transparent)' }}
          />
          <div className="flex items-center justify-center px-3 pb-1.5">
            <div className="flex-1 min-w-0">
              <BuddyThoughtTicker
                mood={bs?.buddyMood ?? 'calm'}
                weather={displayWeather}
                networkState={bs?.networkState ?? 'quiet'}
                blockState={bs?.blockState ?? 'normal'}
                priceState={bs?.priceState ?? 'flat'}
                verdict={displayVerdict}
                sentimentBucket={bs?.sentimentBucket}
                activeSignature={activeSignature}
                blocksToHalving={network.blocksToHalving}
                hashrateChange7d={network.hashrateChange7d}
                lightningVitalityScore={Math.min(1, (network.lightningCapacitySats ?? 0) / (5000 * 1e8))}
                footerLine={displayFooterLine}
                macroState={displayMacroState}
                seasonState={bs?.seasonState}
              />
            </div>
          </div>
        </motion.div>

        {/* Expanded HUD details — drops below the capsule */}
        <AnimatePresence>
          {hudExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
              className="overflow-hidden mt-1.5"
            >
              <div
                className="rounded-2xl px-3.5 py-2.5 space-y-2"
                style={{
                  background: 'hsl(var(--foreground) / 0.06)',
                  border: '1px solid hsl(var(--foreground) / 0.07)',
                  backdropFilter: 'blur(16px)',
                  boxShadow: '0 1px 8px hsl(var(--background) / 0.2), inset 0 1px 0 hsl(var(--foreground) / 0.03)',
                }}
              >
                {/* Mempool */}
                <div className="space-y-1">
                  <span className="text-[7px] font-body font-medium tracking-widest uppercase" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                    Mempool
                  </span>
                  <div className="flex gap-3">
                    <HudDetailRow label="Size" value={`${((network.mempoolVsize ?? 0) / 1_000_000).toFixed(1)} MvB`} />
                    <HudDetailRow label="TXs" value={(network.pendingTxCount ?? 0).toLocaleString()} />
                    <HudDetailRow label="Fees" value={`${network.recommendedFees.low}/${network.recommendedFees.medium}/${network.recommendedFees.high}`} />
                  </div>
                </div>
                {/* Mining */}
                <div className="space-y-1">
                  <span className="text-[7px] font-body font-medium tracking-widest uppercase" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                    Mining
                  </span>
                  <div className="flex gap-3">
                    <HudDetailRow
                      label="Hashrate"
                      value={`${(network.hashrateChange7d ?? 0) > 0 ? '↑' : (network.hashrateChange7d ?? 0) < 0 ? '↓' : ''}${((network.hashrateTHs ?? 0) / 1e6).toFixed(0)} EH/s`}
                    />
                    <HudDetailRow label="Adj." value={`${(network.difficultyChange ?? 0) >= 0 ? '+' : ''}${(network.difficultyChange ?? 0).toFixed(1)}%`} />
                    <HudDetailRow label="Epoch" value={`${(network.difficultyProgress ?? 0).toFixed(1)}%`} />
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden mt-1.5" style={{ background: 'hsl(var(--foreground) / 0.06)' }}>
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: 'hsl(var(--bitcoin) / 0.4)' }}
                      animate={{ width: `${network.difficultyProgress ?? 0}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                    />
                  </div>
                </div>
                {/* Halving */}
                <div className="flex items-center justify-between">
                  <span className="text-[7px] font-body font-medium uppercase tracking-wider" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                    Halving
                  </span>
                  <span
                    className="text-[8px] font-body font-medium tabular-nums"
                    style={{
                      color: (network.blocksToHalving ?? 0) < 144
                        ? 'hsl(var(--bitcoin) / 0.9)'
                        : (network.blocksToHalving ?? 0) < 2016
                          ? 'hsl(var(--bitcoin) / 0.7)'
                          : 'hsl(var(--foreground) / 0.5)',
                    }}
                  >
                    {(network.blocksToHalving ?? 0) < 144
                      ? '🎰 TOMORROW'
                      : `${(network.blocksToHalving ?? 0).toLocaleString()} blocks · ~${Math.round(network.daysToHalving ?? 0)}d`}
                  </span>
                </div>
                {/* Lightning */}
                {(network.lightningCapacitySats ?? 0) > 0 && (
                  <div className="space-y-1">
                    <span className="text-[7px] font-body font-medium tracking-widest uppercase" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                      ⚡ Lightning
                    </span>
                    <div className="flex gap-3">
                      <HudDetailRow label="Capacity" value={`${Math.round((network.lightningCapacitySats ?? 0) / 1e8).toLocaleString()} BTC`} />
                      <HudDetailRow label="Channels" value={(network.lightningChannels ?? 0).toLocaleString()} />
                      <HudDetailRow label="Nodes" value={(network.lightningNodes ?? 0).toLocaleString()} />
                    </div>
                    <div className="h-0.5 rounded-full overflow-hidden mt-1" style={{ background: 'hsl(var(--foreground) / 0.04)' }}>
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: 'hsl(var(--success) / 0.3)' }}
                        animate={{ width: `${Math.min(100, ((network.lightningCapacitySats ?? 0) / (5000 * 1e8)) * 100)}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                )}
                {/* Last block */}
                <div className="flex items-center justify-between">
                  <span className="text-[7px] font-body font-medium uppercase tracking-wider" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                    Last block
                  </span>
                  <span className="text-[8px] font-body font-medium tabular-nums" style={{ color: 'hsl(var(--foreground) / 0.5)' }}>
                    {Math.floor((network.timeSinceLastBlock ?? 0) / 60)}m {(network.timeSinceLastBlock ?? 0) % 60}s ago
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
      )}
      </AnimatePresence>

      {/* Buddy — absolutely positioned so HUD expand doesn't move it */}
      <AnimatePresence>
      {stackSats.phase === 'idle' && (
      <motion.div
        className="absolute inset-0 z-20 pointer-events-none"
        initial={false}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.3 } }}
      >
      <div
        ref={buddyCaptureRef}
        style={{
          transform: `translateX(-50%) scale(${buddyScale})`,
          transformOrigin: 'center bottom',
          top: viewport.width <= 768
            ? `${Math.min(34, 26 + Math.max(0, (viewport.height / Math.max(viewport.width, 1) - 1.75) * 8)).toFixed(2)}%`
            : '24%',
          position: 'absolute',
          left: '50%',
          filter: matrixMode ? 'none' : deriveSceneLighting({ macroState: (displayMacroState || 'NEUTRAL') as MacroLightState, mempoolPressure: 0, blockJustFound: false, heatLevel: 0 }).buddyFilter,
          transition: 'filter 2s ease',
        }}
      >
        <div
          className="relative flex flex-col items-center cursor-pointer pointer-events-auto"
          onClick={() => { if (!matrixMode) setShowBarometer(true); }}
        >
          {/* Block-found warm glow around Buddy */}
          <AnimatePresence>
            {blockJustFound && (
              <motion.div
                className="absolute inset-0 z-0 rounded-full"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 0.55, scale: 1.35 }}
                exit={{ opacity: 0, scale: 1.5 }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
                style={{
                  background: 'radial-gradient(circle, hsla(35, 75%, 60%, 0.35) 0%, hsla(35, 70%, 55%, 0.12) 45%, transparent 70%)',
                  filter: 'blur(8px)',
                }}
              />
            )}
          </AnimatePresence>
          <BuddyCharacter
              composed={composed}
              activeSignature={activeSignature}
              heatLevel={heatLevel}
              blockRelief={blockJustFound}
              seasonState={bs?.seasonState}
              weather={displayWeather}
              lightningVitality={environment?.lightningVitality ?? 0.5}
              displayMacroState={bs?.displayMacroState ?? displayMacroState}
            />
        </div>
      </div>
      </motion.div>
      )}
      </AnimatePresence>

      {/* Barometer expanded overlay — triggered by tapping Buddy */}
      <IslandBarometer
        macroState={bs?.displayMacroState ?? displayMacroState ?? 'NEUTRAL'}
        moodLabel={displayMoodLabel}
        plainSubtitle={displayPlainSubtitle}
        whatItMeans={displayWhatItMeans}
        advisory={macroAdvisory}
        expanded={showBarometer}
        onClose={() => setShowBarometer(false)}
        networkState={bs?.networkState}
        blockState={bs?.blockState}
        activeSignature={activeSignature}
        heatLevel={heatLevel}
        recentEventHeadline={displayEvents[0]?.headline ?? null}
        ltConfidence={bs?.ltConfidence}
      />

      {/* Spacer to push footer down */}
      <div className="flex-1" />


      {/* Debug panel (legacy — double-click title) */}
      <AnimatePresence>
        {showDebug && hasBackend && (
          <motion.div data-share-exclude="true"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-0 inset-x-0 z-50 p-3 max-h-[40vh] overflow-y-auto"
            style={{
              background: 'hsl(var(--background) / 0.95)',
              borderTop: '1px solid hsl(var(--border) / 0.3)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <div className="max-w-sm mx-auto space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[8px] font-pixel text-bitcoin/50 tracking-wider">DEBUG</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSimulateBlock(true)}
                    className="text-[7px] font-body font-medium px-2 py-0.5 rounded bg-bitcoin/20 text-bitcoin/70 hover:bg-bitcoin/30 transition-colors"
                  >
                    ⛏ Test Block
                  </button>
                  <button
                    onClick={toggleDebug}
                    className="text-[8px] font-body text-muted-foreground/40 hover:text-muted-foreground/60"
                  >
                    close
                  </button>
                </div>
              </div>
              <DebugRow label="Network" value={bs.networkState} />
              <DebugRow label="Block" value={bs.blockState} />
              <DebugRow label="Price" value={bs.priceState} />
              <DebugRow label="Mood" value={bs.buddyMood} />
              <DebugRow label="Weather" value={bs.weather} />
              <DebugRow label="Heat" value={Number(bs.heatLevel).toFixed(3)} />
              <DebugRow label="Signature" value={bs.activeSignature ?? 'none'} />
              <DebugRow label="Transient" value={transientManager.activeTransient ?? 'none'} />
              <DebugRow label="Tr count" value={String(transientManager.debug.triggerCount)} />
              <DebugRow label="Sentiment" value={bs.sentimentBucket ? `${bs.sentimentValue} (${bs.sentimentBucket})` : 'n/a'} />
              <DebugRow label="Confidence" value={bs.ltConfidence} />
              <DebugRow label="Net mood" value={bs.networkMood} />
              <DebugRow label="Mkt mood" value={bs.marketMood} />
              <DebugRow label="Blk mood" value={bs.blockMood} />
              <DebugRow label="Verdict" value={bs.verdict} />
              <DebugRow label="Archetype" value={bs.dayArchetype} />
              <DebugRow label="Summary" value={bs.dailySummary} />
              <DebugRow label="Weekly" value={bs.weeklyVibe} />
              <DebugRow label="Snapshots" value={String(backend.meta.snapshotCount)} />
              <DebugRow label="Worker" value={backend.meta.workerStatus} />
              <DebugRow label="Stale sec" value={String(backend.meta.staleSec)} />
              <DebugRow label="Updated" value={new Date(bs.updatedAt).toLocaleTimeString()} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Dashboard removed — merged into Guide */}

      {/* Footer: News ticker banner */}
      <AnimatePresence>
      {stackSats.phase === 'idle' && (
      <motion.footer data-share-exclude="true" className="w-full max-w-sm mx-auto px-3 pb-3 pt-0 relative z-10" style={{ position: 'sticky', bottom: 0 }}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} transition={{ duration: 0.3 }}>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="rounded-xl overflow-hidden"
          style={{
            background: 'hsl(var(--foreground) / 0.08)',
            border: '1px solid hsl(var(--foreground) / 0.09)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 -2px 16px hsl(var(--background) / 0.3), inset 0 1px 0 hsl(var(--foreground) / 0.04)',
          }}
        >
          {/* News ticker — hidden in matrix mode */}
          {!matrixMode && (
            <>
              <NewsTicker newsItems={bs?.newsItems} />
              <div
                className="mx-3"
                style={{ height: 1, background: 'linear-gradient(90deg, transparent, hsl(var(--foreground) / 0.08), transparent)' }}
              />
            </>
          )}
          {/* Stack Sats button — matrix mode only */}
          {matrixMode && stackSats.phase === 'idle' && (
            <div className="px-3.5 py-2">
              <button
                onClick={() => stackSats.startGame()}
                className="w-full py-3 px-4 rounded-lg flex items-center justify-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.2em] transition-all duration-200 active:scale-95"
                style={{
                  fontFamily: 'monospace',
                  color: 'rgba(0,255,70,0.9)',
                  background: 'rgba(0,255,70,0.06)',
                  border: '1px solid rgba(0,255,70,0.2)',
                  boxShadow: '0 0 20px rgba(0,255,70,0.08), inset 0 1px 0 rgba(0,255,70,0.06)',
                  textShadow: '0 0 12px rgba(0,255,70,0.4)',
                  animation: 'stack-sats-glow 2.5s ease-in-out infinite, stack-sats-border 3s ease-in-out infinite',
                }}
              >
                <span className="animate-[stack-sats-icon_3s_ease-in-out_infinite]" style={{ fontSize: '16px' }}>₿</span>
                Stack Sats
                <span className="text-[8px] tracking-wider" style={{ color: 'rgba(0,255,70,0.4)' }}>▶</span>
              </button>
            </div>
          )}
          {/* Divider before matrix toggle in matrix mode */}
          {matrixMode && (
            <div
              className="mx-3"
              style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,255,70,0.12), transparent)' }}
            />
          )}
          <div className="flex flex-col items-center px-3.5 pt-2.5 pb-1.5 gap-1">
            <button
              onClick={() => {
                if (!matrixMode) {
                  setMatrixSplash(true);
                } else {
                  setMatrixMode(false);
                }
              }}
              className="group flex items-center justify-center gap-2.5 text-[11px] font-body font-bold tracking-[0.15em] uppercase transition-all duration-200 w-full py-2 px-4 rounded-lg active:scale-95"
              style={matrixMode ? {
                color: 'hsl(210 90% 65% / 0.95)',
                fontFamily: 'monospace',
                background: 'rgba(60,130,246,0.08)',
                border: '1px solid rgba(60,130,246,0.25)',
                boxShadow: '0 0 24px rgba(60,130,246,0.12), inset 0 1px 0 rgba(60,130,246,0.08)',
                textShadow: '0 0 12px rgba(60,130,246,0.4)',
                animation: 'exit-matrix-border 3s ease-in-out infinite',
              } : {
                color: 'hsl(0 72% 58% / 1)',
                background: 'rgba(220,38,38,0.08)',
                border: '1px solid rgba(220,38,38,0.3)',
                boxShadow: '0 0 24px rgba(220,38,38,0.12), inset 0 1px 0 rgba(220,38,38,0.08)',
                textShadow: '0 0 12px rgba(220,38,38,0.35)',
                animation: 'pill-pulse 2.5s ease-in-out infinite',
              }}
            >
              {matrixMode ? (
                <>
                  <span className="inline-block w-[10px] h-[10px] rounded-full shadow-[0_0_10px_rgba(60,130,246,0.7),0_0_20px_rgba(60,130,246,0.4)] animate-pulse" style={{ background: 'hsl(210 85% 55%)' }} />
                  exit matrix
                </>
              ) : (
                <>
                  <span className="inline-block w-[10px] h-[10px] rounded-full bg-red-500 shadow-[0_0_10px_rgba(220,38,38,0.7),0_0_20px_rgba(220,38,38,0.4)] animate-pulse" />
                  show me the matrix
                </>
              )}
            </button>
            {/* Matrix mode: fee & block stats row */}
            {matrixMode ? (
              <p
                className="text-[8px] tabular-nums tracking-wide text-center"
                style={{
                  fontFamily: 'monospace',
                  color: 'rgba(0,255,70,0.5)',
                  textShadow: '0 0 6px rgba(0,255,70,0.25)',
                }}
              >
                ⚡ {network.recommendedFees.high} · {network.recommendedFees.medium} · {network.recommendedFees.low} sat/vB &nbsp;│&nbsp; #{network.lastBlockHeight.toLocaleString()}
              </p>
            ) : (
              <div className="flex items-center gap-1.5">
                <div
                  className="w-[5px] h-[5px] rounded-full shrink-0"
                  style={{
                    background: displayMacroState === 'DEEP_VALUE' ? 'hsl(var(--energy))' :
                      displayMacroState === 'ACCUMULATION' ? 'hsl(var(--success))' :
                      displayMacroState === 'WARM' ? 'hsl(var(--bitcoin))' :
                      displayMacroState === 'HOT' ? 'hsl(var(--stress))' :
                      displayMacroState === 'MIXED' ? 'hsl(var(--mood))' :
                      'hsl(var(--foreground) / 0.25)',
                    boxShadow: displayMacroState !== 'NEUTRAL'
                      ? `0 0 4px ${displayMacroState === 'DEEP_VALUE' ? 'hsl(var(--energy) / 0.4)' :
                          displayMacroState === 'ACCUMULATION' ? 'hsl(var(--success) / 0.4)' :
                          displayMacroState === 'WARM' ? 'hsl(var(--bitcoin) / 0.4)' :
                          displayMacroState === 'HOT' ? 'hsl(var(--stress) / 0.4)' :
                          'hsl(var(--mood) / 0.3)'}`
                      : 'none',
                  }}
                />
                <p className="text-[7px] font-body font-light tracking-wide" style={{ color: 'hsl(var(--foreground) / 0.22)' }}>
                  live data · mempool.space + coingecko
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.footer>
      )}
      </AnimatePresence>

      {/* Share Modal */}
      {shareCapture && (
        <ShareModal
          open={!!shareCapture}
          onClose={() => setShareCapture(null)}
          imageDataUrl={shareCapture}
          btcPrice={network.btcPrice}
          mediumFee={network.recommendedFees.medium}
          blockHeight={network.lastBlockHeight}
          archetype={displayArchetype}
          verdict={displayVerdict}
          dailySummary={bs?.dailySummary}
          weeklyVibe={bs?.weeklyVibe}
          moodLabel={displayMoodLabel}
          plainSubtitle={displayPlainSubtitle}
          macroExplanation={displayMacroExplanation}
          footerLine={displayFooterLine}
          displayMacroState={displayMacroState}
        />
      )}

      {/* Buddy Guide (includes History + Info) */}
      <BuddyGuide
        open={showGuide}
        onClose={() => setShowGuide(false)}
        bs={bs}
        network={network}
        events={backend.events}
        meta={backend.meta}
        activeTransient={transientManager.activeTransient}
        transientDebug={transientManager.debug}
        weeklyVibe={bs?.weeklyVibe}
        dailySummary={bs?.dailySummary}
        onResetOnboarding={() => {
          setShowGuide(false);
          onboarding.reset();
        }}
      />

      {/* Onboarding */}
      <OnboardingSheet show={onboarding.needsOnboarding} onComplete={onboarding.complete} />
    </div>
    </Suspense>
  );
};

function DebugRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[7px] font-body text-muted-foreground/30 uppercase tracking-wider shrink-0">{label}</span>
      <span className="text-[8px] font-body text-muted-foreground/60 text-right truncate">{value}</span>
    </div>
  );
}
function HudDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 min-w-0">
      <span className="text-[6px] font-body font-medium uppercase tracking-wider block" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>{label}</span>
      <span className="text-[9px] font-body font-semibold tabular-nums block truncate" style={{ color: 'hsl(var(--foreground) / 0.7)' }}>{value}</span>
    </div>
  );
}
function FeeBar({ level, max }: { level: number; max: number }) {
  const h = Math.max(3, Math.min(12, (level / max) * 12));
  const intensity = level / max;
  return (
    <motion.div
      className="w-[3px] rounded-full"
      animate={{ height: h }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      style={{
        background: intensity > 0.6
          ? 'hsl(var(--stress) / 0.5)'
          : intensity > 0.3
            ? 'hsl(var(--bitcoin) / 0.4)'
            : 'hsl(var(--success) / 0.4)',
      }}
    />
  );
}
function FeePopoverRow({ label, value, tier }: { label: string; value: number; tier: 'low' | 'mid' | 'high' }) {
  const color = tier === 'high' ? 'hsl(var(--stress) / 0.7)' : tier === 'mid' ? 'hsl(var(--bitcoin) / 0.6)' : 'hsl(var(--success) / 0.6)';
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
        <span className="text-[9px] font-body font-medium" style={{ color: 'hsl(var(--foreground) / 0.6)' }}>{label}</span>
      </div>
      <span className="text-[10px] font-body font-semibold tabular-nums" style={{ color: 'hsl(var(--foreground) / 0.8)' }}>
        {value} <span className="text-[7px] font-light" style={{ color: 'hsl(var(--muted-foreground) / 0.4)' }}>sat/vB</span>
      </span>
    </div>
  );
}
function FeeInline({ label, value }: { label: string; value: number }) {
  return (
    <span className="text-[9px] font-body tabular-nums" style={{ color: 'hsl(var(--foreground) / 0.55)' }}>
      <span className="font-light" style={{ color: 'hsl(var(--muted-foreground) / 0.4)' }}>{label}</span>{' '}
      <span className="font-medium">{value}</span>
    </span>
  );
}
const SIGNATURE_LABEL: Record<string, string> = {
  queueStare: '👀 QUEUE STARE',
  minerPrayer: '🙏 MINER PRAYER',
  reliefExhale: '😮‍💨 RELIEF',
  crabDeadpan: '🦀 DEADPAN',
  laserFlex: '⚡ LASER FLEX',
  feeFeverTwitch: '🔥 FEE FEVER',
};

function NewsTicker({ newsItems }: { newsItems?: NewsItem[] }) {
  const items = newsItems;
  const isLoading = items === undefined;
  const isEmpty = !isLoading && (!items || items.length === 0);

  const trackRef = useRef<HTMLDivElement>(null);
  const [loopWidth, setLoopWidth] = useState(0);

  // Content fingerprint — only re-measure when headlines actually change
  const fingerprint = useMemo(() => items?.map(i => i.title).join('|') ?? '', [items]);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || !items?.length) return;
    // scrollWidth contains both copies; half = one set's width
    const measure = () => setLoopWidth(el.scrollWidth / 2);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fingerprint]);

  const scrollDuration = Math.max(30, (items?.length ?? 0) * 15);

  if (isLoading) {
    return (
      <div className="px-3.5 py-2 overflow-hidden">
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-body font-medium" style={{ color: 'hsl(var(--foreground) / 0.4)' }}>
            ⚡ Loading Bitcoin news...
          </span>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="px-3.5 py-2 overflow-hidden">
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-body font-medium" style={{ color: 'hsl(var(--foreground) / 0.35)' }}>
            📡 Live data · mempool.space + coingecko
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3.5 py-2 overflow-hidden">
      <div className="flex items-center gap-2">
        <motion.div
          className="w-1 h-1 rounded-full shrink-0"
          style={{ background: 'hsl(var(--bitcoin) / 0.5)' }}
          animate={{ opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="text-[8px] font-body font-semibold tracking-widest uppercase shrink-0" style={{ color: 'hsl(var(--bitcoin) / 0.75)' }}>
          News
        </span>
        <div className="flex-1 overflow-hidden">
          <div
            ref={trackRef}
            className="flex whitespace-nowrap w-max"
            style={loopWidth > 0 ? {
              animation: `ticker-slide ${scrollDuration}s linear infinite`,
            } : undefined}
          >
            {[...items!, ...items!].map((item, i) => {
              const src = item.source.length > 16 ? item.source.slice(0, 15) + '…' : item.source;
              const label = `${src}  ·  ${item.title}`;
              return (
                <span key={`${i}-${item.title}`} className="inline-flex items-center shrink-0">
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] font-body font-medium hover:underline"
                      style={{ color: 'hsl(var(--foreground) / 0.65)' }}
                      onClick={e => e.stopPropagation()}
                    >
                      {label}
                    </a>
                  ) : (
                    <span className="text-[11px] font-body font-medium" style={{ color: 'hsl(var(--foreground) / 0.55)' }}>
                      {label}
                    </span>
                  )}
                  <span className="text-[10px] mx-2.5" style={{ color: 'hsl(var(--foreground) / 0.3)' }}>⚡</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
      {/* Pixel-perfect keyframes — only re-created when loopWidth changes */}
      {loopWidth > 0 && (
        <style>{`@keyframes ticker-slide { from { transform: translateX(0); } to { transform: translateX(-${loopWidth}px); } }`}</style>
      )}
    </div>
  );
}

export default Index;
